import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import type { FormField } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';
import { Box, Checkbox, Chip, InputAdornment, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Button } from '../../components/button';
import { TextField } from '../../components/text-field';
import { nameOf, tr } from '../../lib/i18n';
import { matches } from '../../lib/search';
import { universe, type Dim, type Quick } from './visibility';

const TITLE: Record<Dim, () => string> = {
  request_type: () => tr('نوع الطلب', 'Request type'),
  property_category: () => tr('فئة العقار', 'Property category'),
  property_type: () => tr('نوع العقار', 'Property type'),
};

interface Props {
  value: Quick;
  onChange: (q: Quick) => void;
  dims: Partial<Record<Dim, FormField>>;
  lookups?: Lookups;
}

/** Chips for the three common filters. Nothing selected on a row = shown for all of it. */
export function QuickVisibility({ value, onChange, dims, lookups }: Props) {
  const present = (['request_type', 'property_category', 'property_type'] as Dim[]).filter((d) => dims[d]);
  if (!lookups) return null;
  if (!present.length) {
    return <Typography variant="body2" color="text.secondary">{tr('أضف حقل «نوع الطلب» أو «فئة/نوع العقار» قبل هذا الحقل لتحديد من يراه.', 'Add a “Request type” or “Property category/type” field above this one to choose who sees it.')}</Typography>;
  }
  const setDim = (d: Dim, codes: string[] | undefined) => onChange({ ...value, sets: { ...value.sets, [d]: codes } });

  return (
    <Stack spacing={2}>
      {present.map((d) => {
        const all = universe(d, lookups);
        const set = value.sets[d];
        const chosen = set ?? all;
        const toggle = (code: string) => {
          const next = chosen.includes(code) ? chosen.filter((x) => x !== code) : [...chosen, code];
          setDim(d, next.length === all.length && all.every((x) => next.includes(x)) ? undefined : next);
        };
        const status = set === undefined
          ? tr('الكل', 'All')
          : set.length === 0
            ? tr('لا أحد — الحقل لن يظهر', 'None — the field never shows')
            : tr(`${set.length} من ${all.length}`, `${set.length} of ${all.length}`);
        return (
          <Box key={d}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>{TITLE[d]()}</Typography>
              <Typography variant="caption" color={set?.length === 0 ? 'error' : 'text.secondary'} sx={{ flex: 1 }}>{status}</Typography>
              <Button size="small" onClick={() => setDim(d, undefined)} disabled={set === undefined}>{tr('الكل', 'All')}</Button>
              <Button size="small" onClick={() => setDim(d, [])} disabled={set?.length === 0}>{tr('لا شيء', 'None')}</Button>
            </Stack>
            {d === 'property_type' ? (
              <TypePicker lookups={lookups} chosen={chosen} categoryFilter={value.sets.property_category} onToggle={toggle} onSet={(codes) => setDim(d, codes.length === all.length ? undefined : codes)} />
            ) : (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                {(d === 'request_type' ? lookups.requestTypes : lookups.categories).map((r) => (
                  <CodeChip key={r.code} label={nameOf(r)} on={chosen.includes(r.code)} onClick={() => toggle(r.code)} />
                ))}
              </Stack>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function CodeChip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      icon={on ? <CheckIcon /> : undefined}
      color={on ? 'primary' : 'default'}
      variant={on ? 'filled' : 'outlined'}
      sx={{ fontWeight: on ? 600 : 400, opacity: on ? 1 : 0.75 }}
    />
  );
}

/** Property types grouped by category, each group with a select-all box, plus search. */
function TypePicker({ lookups, chosen, categoryFilter, onToggle, onSet }: { lookups: Lookups; chosen: string[]; categoryFilter?: string[]; onToggle: (c: string) => void; onSet: (codes: string[]) => void }) {
  const [q, setQ] = useState('');
  const groups = useMemo(
    () =>
      lookups.categories.map((c) => ({
        cat: c,
        types: lookups.propertyTypes.filter((t) => t.category_id === c.id),
      })),
    [lookups],
  );
  return (
    <Stack spacing={1}>
      {lookups.propertyTypes.length > 12 && (
        <TextField size="small" placeholder={tr('ابحث عن نوع…', 'Search types…')} value={q} onChange={(e) => setQ(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      )}
      {groups.map(({ cat, types }) => {
        const shown = types.filter((t) => matches(q, t.name_ar, t.name_en, t.code, cat.name_ar, cat.name_en));
        if (!shown.length) return null;
        const on = types.filter((t) => chosen.includes(t.code)).length;
        const outOfCategory = categoryFilter !== undefined && !categoryFilter.includes(cat.code);
        const codes = types.map((t) => t.code);
        return (
          <Box key={cat.code} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1, opacity: outOfCategory ? 0.55 : 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Checkbox
                size="small"
                checked={on === types.length && types.length > 0}
                indeterminate={on > 0 && on < types.length}
                onChange={(e) => onSet(e.target.checked ? Array.from(new Set([...chosen, ...codes])) : chosen.filter((x) => !codes.includes(x)))}
                sx={{ p: 0.5 }}
              />
              <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>{nameOf(cat)}</Typography>
              <Typography variant="caption" color="text.secondary">{on}/{types.length}</Typography>
            </Stack>
            {outOfCategory && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{tr('هذه الفئة غير مختارة أعلاه، فلن تظهر أنواعها.', 'This category is not selected above, so its types never show.')}</Typography>}
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
              {shown.map((t) => <CodeChip key={t.code} label={nameOf(t)} on={chosen.includes(t.code)} onClick={() => onToggle(t.code)} />)}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
